import { onMounted, onUnmounted } from 'vue';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

const WHITELISTED_QUERY_PARAMS = ['redirectURL'];
const anonymizeUrl = (url: string) => {
  // redact all query parameters from the url that are not redirect urls
  const [baseUrl, queryString] = url.split('?');
  if (!queryString) {
    return url;
  }

  const params = new URLSearchParams(queryString);
  for (const key of params.keys()) {
    if (!WHITELISTED_QUERY_PARAMS.includes(key)) {
      params.set(key, '***');
    }
  }

  return `${baseUrl}?${params.toString()}`;
};
const createViewName = (url: string) => {
  const { pathname } = new URL(url);

  return pathname;
};

export const useDatadog = () => {
  onMounted(() => {
    datadogLogs.init({
      clientToken: import.meta.env.VITE_DATADOG_LOG_TOKEN,
      site: import.meta.env.VITE_DATADOG_SITE,
      service: import.meta.env.VITE_DATADOG_SERVICE,
      version: import.meta.env.VITE_DATADOG_VERSION,
      env: import.meta.env.VITE_ENV,
      // Uncaught errors and unhandled rejections only. Since SDK v7 this no
      // longer implies `forwardConsoleLogs: ['error']`, and we do not add it
      // back: every console.error in this app either sits next to an explicit
      // datadogLogs.logger.error (main.ts errorHandler, useLogger) or is
      // local-only debug output, so forwarding would duplicate each entry.
      forwardErrorsToLogs: true,
      sessionSampleRate: Number(import.meta.env.VITE_DATADOG_LOG_SAMPLE_RATE),
      beforeSend: (event) => {
        event.view.url = anonymizeUrl(event.view.url);

        if (event.view.referrer) {
          event.view.referrer = anonymizeUrl(event.view.referrer);
        }
        if (event.path) {
          event.path = anonymizeUrl(event.path as string);
        }

        return true;
      },
    });

    datadogRum.init({
      applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
      clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
      site: import.meta.env.VITE_DATADOG_SITE,
      service: import.meta.env.VITE_DATADOG_SERVICE,
      env: import.meta.env.VITE_ENV,
      version: import.meta.env.VITE_DATADOG_VERSION,
      // RUM without Limits: the application has `rum_event_processing_scale:
      // ALL`, so ingestion is decoupled from indexing — Datadog keeps every
      // session it receives and the retention filters decide what is stored
      // and billed. Any client-side sampling below 100 is thrown away before
      // those filters ever see it, so both rates are fixed here rather than
      // exposed per environment. Tune volume in the Datadog UI
      // (RUM > Manage Applications > Amplify auth > Retention Filters).
      sessionSampleRate: 100,
      // A retention filter can only keep a replay the browser actually
      // recorded, so this has to stay at 100 for the "Sessions with errors"
      // filter (replay 100%) to return a video.
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      // Default since SDK v7, pinned explicitly: these are auth screens, and
      // action names are computed from the DOM, which here can hold an email
      // address or a customer name.
      enablePrivacyForActionName: true,
      beforeSend: (event) => {
        event.view.url = anonymizeUrl(event.view.url);
        event.view.name = createViewName(event.view.url);

        if (event.view.referrer) {
          event.view.referrer = anonymizeUrl(event.view.referrer);
        }
        if (event.type === 'resource' && event.resource.url) {
          event.resource.url = anonymizeUrl(event.resource.url);
        }
        if (event.type === 'error' && event.error.resource?.url) {
          event.error.resource.url = anonymizeUrl(event.error.resource.url);
        }

        return true;
      },
    });

    const browserLanguage = navigator.language;
    datadogRum.setGlobalContextProperty('@browser.lang', browserLanguage);
  });

  onUnmounted(() => {
    datadogRum.stopSessionReplayRecording();
  });
};
